import { Component, Prop, State, h } from '@stencil/core';
import { casinoMiniWidget, chevronRight, close } from '../../globals/icons';
import { fetchLocale, getTranslation } from '../../utils/locale/locale';
import state from '../../store/settings';
import { setFonts } from '../../utils/utils';
import { messageHandler } from '../../utils/message-handler';
import { fetchCategory } from '../../utils/lobbies-api';
import { IGame } from '../../components';

@Component({
  tag: 'casino-mini-widget',
  styleUrl: 'casino-mini-widget.scss',
  shadow: true,
})
export class CasinoMiniWidget {
  dragStart = false;
  scrollingRow: HTMLElement;
  scrollPos: { left: number; mouseXPos: number };

  @State() topGames: IGame[] = [];
  @State() topGamesLoading = true;
  @State() isMobileBreakpoint = false;

  @Prop() vertical: string;
  @Prop() category: string;
  @Prop() localeCode = 'en-US';
  @Prop() languageCode: string;
  @Prop() regionCode: string;
  @Prop() currency: string;
  @Prop() channel: string;
  @Prop() environment = 'Production';
  @Prop() primaryColour: string;
  @Prop() betslipMode: boolean = false;

  async componentWillLoad() {
    const locales = await fetchLocale(`${state.cmsDomain}/gmapi/Resource/GetByCulture?culture=${this.localeCode}&key=synapse.lobbies`);
    setFonts();
    document.documentElement.style.setProperty('--primary-colour', this.primaryColour);

    state.locales = locales;
    state.environment = this.environment;

    const media = matchMedia('(max-width: 960px)');
    this.isMobileBreakpoint = media.matches;

    media.onchange = (_event: MediaQueryListEvent) => {
      this.isMobileBreakpoint = media.matches;
    };
  }

  async componentDidLoad() {
    await this.fetchCatgory();
  }

  async fetchCatgory() {
    try {
      const res = await fetchCategory({
        regionCode: this.regionCode,
        vertical: this.vertical,
        category: this.category,
        channel: this.channel,
        languageCode: this.languageCode,
        skip: 0,
        limit: 12,
        currency: this.currency,
        appVersion: '0',
      });
      this.topGames = res.data;
    } catch (error) {
      console.error(error);
    }
    this.topGamesLoading = false;
  }

  stopDrag() {
    this.dragStart = false;
  }

  dragInit(e: MouseEvent) {
    this.dragStart = true;
    this.scrollPos = {
      left: this.scrollingRow.scrollLeft,
      mouseXPos: e.clientX,
    };
  }

  handleHorizontalScroll(e: MouseEvent) {
    if (!this.dragStart) return;
    const scrollAmount = e.clientX - this.scrollPos.mouseXPos;
    this.scrollingRow.scrollLeft = this.scrollPos.left - scrollAmount;
  }

  navigateToCasino() {
    messageHandler({
      message: 'navigate',
      data: {
        to: 'lobby',
        vertical: this.vertical,
        categoryName: 'all',
      },
    });
  }

  closeWidget(e: Event) {
    e.stopPropagation();
    messageHandler({
      message: 'close-mini-widget',
      data: null,
    });
  }

  handleImageError(event: Event): void {
    (event.target as HTMLImageElement).src = `${state.cmsDomain}/medialibraries/content.gmgamingsystems.com/Synapse/icons/lobbies/betwayexclusives.svg`;
  }

  render() {
    return (
      <div class="main-container">
        {this.betslipMode.toString() !== 'true' && (
          <ripple-button class="header" classBinding={'button casino'} onClick={this.navigateToCasino}>
            <div>
              {!this.isMobileBreakpoint ? (
                <svg class="casino-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="img" aria-hidden="true" width="24" height="24" fill="#0075c6">
                  <path d={casinoMiniWidget} />
                </svg>
              ) : (
                ''
              )}
              <div>
                <span>{getTranslation({ toTranslate: 'Casino Games' })}</span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="img" aria-hidden="true" width="24" fill="white">
                  <path d={chevronRight} />
                </svg>
              </div>
            </div>
            {this.isMobileBreakpoint ? (
              <ripple-button classBinding={'button close'} onClick={e => this.closeWidget(e)}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="img" aria-hidden="true" width="24" fill="white">
                  <path d={close} />
                </svg>
              </ripple-button>
            ) : (
              ''
            )}
            {this.isMobileBreakpoint ? <div class="bottom-bar" /> : ''}
          </ripple-button>
        )}
        <section
          ref={el => (this.scrollingRow = el)}
          onMouseDown={e => this.dragInit(e)}
          onMouseUp={_e => this.stopDrag()}
          onMouseLeave={_e => this.stopDrag()}
          onMouseMove={e => this.handleHorizontalScroll(e)}
          class={`body ${this.betslipMode.toString() === 'true' ? 'betslip-mode-body' : ''}`}
        >
          {this.betslipMode.toString() === 'true' && (
            <div style={{ boxShadow: '0 3px 5px -1px rgba(0, 0, 0, 0.1), 0 1px 5px -1px rgba(0, 0, 0, 0.06)', borderRadius: '4px' }} onClick={this.navigateToCasino}>
              <div
                class="game-tile"
                style={{
                  aspectRatio: '1/1',
                  backgroundColor: this.primaryColour,
                  height: '100%',
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                {' '}
                <img
                  class="image"
                  style={{ height: '30px' }}
                  onError={event => this.handleImageError(event)}
                  src={`${state.cmsDomain}/medialibraries/content.gmgamingsystems.com/Synapse/icons/lobbies/${this.category}.svg`}
                />
              </div>
              <div style={{ fontSize: '10px', padding: '2px' }}>{getTranslation({ toTranslate: 'All Games' })}</div>
            </div>
          )}
          {this.topGamesLoading ? (
            <loading-spinner />
          ) : (
            this.topGames
              .filter(g => !g.unavailable)
              .map((game: IGame, i: number) => <casino-mini-widget-game-tile game={game} key={`${game.id}-${i}`} betslipMode={this.betslipMode} />)
          )}
        </section>
      </div>
    );
  }
}
